import { toast } from "sonner";
import { Copy, FileText, ThumbsUp, Search } from "lucide-react";
import { Button } from "@/components/ui/button";

function RadarChart({ axes }) {
  const size = 300;
  const c = size / 2;
  const R = 95;
  const n = axes.length;
  const pt = (i, frac) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    return [c + R * frac * Math.cos(angle), c + R * frac * Math.sin(angle)];
  };
  const ring = (frac) => axes.map((_, i) => pt(i, frac).join(",")).join(" ");
  const valuePoly = axes.map((a, i) => pt(i, Math.max(0.04, a.value / 100)).join(",")).join(" ");
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-xs mx-auto" data-testid="workstyle-radar">
      {[0.33, 0.66, 1].map((f) => (
        <polygon key={f} points={ring(f)} fill="none" stroke="#D8D5CC" strokeWidth="1" />
      ))}
      {axes.map((_, i) => {
        const [x, y] = pt(i, 1);
        return <line key={i} x1={c} y1={c} x2={x} y2={y} stroke="#D8D5CC" strokeWidth="1" />;
      })}
      <polygon points={valuePoly} fill="rgba(255,200,0,0.25)" stroke="#FFC800" strokeWidth="2" />
      {axes.map((a, i) => {
        const [x, y] = pt(i, Math.max(0.04, a.value / 100));
        return <circle key={i} cx={x} cy={y} r="3.5" fill="#FFC800" stroke="#0A0C10" strokeWidth="1" />;
      })}
      {axes.map((a, i) => {
        const [x, y] = pt(i, 1.24);
        return (
          <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
            className="fill-slate-600" style={{ fontSize: 9, fontFamily: "monospace", textTransform: "uppercase" }}>
            {`${a.label_low} ↔ ${a.label_high}`}
          </text>
        );
      })}
    </svg>
  );
}

async function copyText(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard.`);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
      toast.success(`${label} copied to clipboard.`);
    } catch {
      toast.error("Could not copy — your browser blocked clipboard access.");
    }
    document.body.removeChild(ta);
  }
}

export function WorkStyleProfile({ workStyle }) {
  if (!workStyle || workStyle.answered === 0) {
    return (
      <section className="bg-slate-panel border border-steel rounded-sm p-6" data-testid="workstyle-section">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-2">Work-Style Profile</h2>
        <p className="text-sm text-slate-500" data-testid="workstyle-empty">No work-style responses yet. This is populated from the candidate's Register Your Interest answers.</p>
      </section>
    );
  }

  return (
    <section className="bg-slate-panel border border-steel rounded-sm p-6 space-y-6" data-testid="workstyle-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base">Work-Style Profile</h2>
          <p className="text-xs text-slate-500 mt-1">
            Supporting information only, built from the candidate's own Register Your Interest answers
            ({workStyle.answered}/{workStyle.total} answered). It is not a selection score and never determines invite, decline or admission automatically.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => copyText(workStyle.copy_text, "Work-style responses")}
            className="border-steel text-slate-700 bg-transparent hover:border-lomond hover:text-lomond" data-testid="copy-workstyle-btn">
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Work-Style Responses
          </Button>
          <Button size="sm" variant="outline" onClick={() => copyText(workStyle.analysis_prompt, "Analysis prompt")}
            className="border-steel text-slate-700 bg-transparent hover:border-lomond hover:text-lomond" data-testid="copy-analysis-prompt-btn">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Copy Analysis Prompt
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          {workStyle.descriptors.map((d) => (
            <div key={d.key} className="bg-obsidian rounded-sm p-3" data-testid={`workstyle-cat-${d.key}`}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-lomond mb-1.5">{d.label}</p>
              {d.items.length > 0 ? (
                <ul className="space-y-1">
                  {d.items.map((t) => <li key={t} className="text-xs text-slate-600">— {t}</li>)}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">No related answers.</p>
              )}
            </div>
          ))}
          <div className="bg-obsidian rounded-sm p-3" data-testid="workstyle-cat-interview">
            <p className="text-[10px] font-mono uppercase tracking-widest text-lomond mb-1.5">Potential interview areas</p>
            {workStyle.interview_areas.length > 0 ? (
              <ul className="space-y-1">
                {workStyle.interview_areas.map((t) => <li key={t} className="text-xs text-slate-600">— {t}</li>)}
              </ul>
            ) : (
              <p className="text-xs text-slate-500">None flagged from these answers.</p>
            )}
          </div>
        </div>
        <div className="space-y-3">
          <RadarChart axes={workStyle.radar} />
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 text-center">
            Indicative only — never used to invite, decline or admit automatically
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-4" data-testid="workstyle-positives">
          <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 flex items-center gap-1.5 mb-2">
            <ThumbsUp className="h-3 w-3" /> Positive indicators
          </p>
          {workStyle.positives.length > 0 ? (
            <ul className="space-y-1">
              {workStyle.positives.map((t) => <li key={t} className="text-xs text-emerald-800">— {t}</li>)}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">None indicated by these answers.</p>
          )}
        </div>
        <div className="bg-amber-400/10 border border-amber-400/30 rounded-sm p-4" data-testid="workstyle-interview-areas">
          <p className="text-[10px] font-mono uppercase tracking-widest text-amber-700 flex items-center gap-1.5 mb-2">
            <Search className="h-3 w-3" /> Areas to explore at interview
          </p>
          {workStyle.interview_areas.length > 0 ? (
            <ul className="space-y-1">
              {workStyle.interview_areas.map((t) => <li key={t} className="text-xs text-amber-800">— {t}</li>)}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">None flagged. Not reasons to reject on their own.</p>
          )}
        </div>
      </div>

      <div data-testid="workstyle-scenarios">
        <h3 className="font-heading font-bold uppercase tracking-wide text-charcoal text-sm mb-3">Workplace scenario answers</h3>
        <div className="space-y-3">
          {workStyle.scenarios.map((s) => (
            <div key={s.n} className="bg-obsidian rounded-sm p-3" data-testid={`scenario-${s.n}`}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Scenario {s.n}</p>
              <p className="text-xs text-slate-500 mt-0.5 italic">{s.question}</p>
              <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap">{s.answer || "—"}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
